import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Badge, Timeline } from 'react-bootstrap';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

/**
 * Track Booking page component for tracking shipment status.
 * 
 * This component allows users to track their bookings using either
 * booking reference number or tracking number.
 * 
 * @author BagABoard Team
 */
function TrackBooking() {
  const [searchType, setSearchType] = useState('reference'); // 'reference' or 'tracking'
  const [searchValue, setSearchValue] = useState('');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle search input change
   */
  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
    if (error) setError('');
  };

  /**
   * Handle search type change
   */
  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchValue('');
    setBooking(null);
    setError('');
  };

  /**
   * Handle form submission to search for booking
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!searchValue.trim()) {
      setError(`Please enter a ${searchType === 'reference' ? 'booking reference' : 'tracking number'}`);
      return;
    }

    setLoading(true);
    setError('');
    setBooking(null);

    try {
      let bookingData;
      
      if (searchType === 'reference') {
        bookingData = await apiService.bookings.getBookingByReference(searchValue.trim());
      } else {
        bookingData = await apiService.bookings.getBookingByTracking(searchValue.trim());
      }
      
      setBooking(bookingData);
      toast.success('Booking found successfully!');
    } catch (error) {
      console.error('Track booking error:', error);
      
      if (error.response?.status === 404) {
        setError('Booking not found. Please check your reference/tracking number and try again.');
      } else {
        setError('Unable to track booking. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get status badge variant based on booking status
   */
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'CONFIRMED':
        return 'info';
      case 'COLLECTED':
        return 'primary';
      case 'IN_TRANSIT':
        return 'primary';
      case 'OUT_FOR_DELIVERY':
        return 'success';
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  /**
   * Format status for display
   */
  const formatStatus = (status) => {
    return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Get tracking timeline based on booking status
   */
  const getTrackingTimeline = (booking) => {
    const timeline = [
      { status: 'PENDING', label: 'Booking Created', icon: 'fas fa-check-circle' },
      { status: 'CONFIRMED', label: 'Payment Confirmed', icon: 'fas fa-credit-card' },
      { status: 'COLLECTED', label: 'Package Collected', icon: 'fas fa-truck-pickup' },
      { status: 'IN_TRANSIT', label: 'In Transit', icon: 'fas fa-shipping-fast' },
      { status: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: 'fas fa-truck' },
      { status: 'DELIVERED', label: 'Delivered', icon: 'fas fa-box-open' }
    ];

    const currentStatusIndex = timeline.findIndex(item => item.status === booking.status);
    
    return timeline.map((item, index) => ({
      ...item,
      completed: index <= currentStatusIndex,
      current: index === currentStatusIndex
    }));
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={8}>
          {/* Page Header */}
          <div className="text-center mb-5">
            <h1 className="display-5 fw-bold">Track Your Shipment</h1>
            <p className="lead text-muted">
              Enter your booking reference or tracking number to get real-time updates
            </p>
          </div>

          {/* Search Form */}
          <Card className="shadow-sm mb-4">
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit}>
                {/* Search Type Selection */}
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Search By:</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="reference"
                      name="searchType"
                      value="reference"
                      checked={searchType === 'reference'}
                      onChange={handleSearchTypeChange}
                      label="Booking Reference"
                      disabled={loading}
                    />
                    <Form.Check
                      type="radio"
                      id="tracking"
                      name="searchType"
                      value="tracking"
                      checked={searchType === 'tracking'}
                      onChange={handleSearchTypeChange}
                      label="Tracking Number"
                      disabled={loading}
                    />
                  </div>
                </Form.Group>

                {/* Search Input */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className={`fas ${searchType === 'reference' ? 'fa-hashtag' : 'fa-barcode'} me-2`}></i>
                    {searchType === 'reference' ? 'Booking Reference' : 'Tracking Number'}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={searchValue}
                    onChange={handleInputChange}
                    placeholder={`Enter your ${searchType === 'reference' ? 'booking reference (e.g., BAB-2024-001234)' : 'tracking number'}`}
                    disabled={loading}
                    size="lg"
                  />
                </Form.Group>

                {/* Error Message */}
                {error && (
                  <Alert variant="danger" className="mb-3">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                {/* Search Button */}
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-100"
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-2"></i>
                      Track Shipment
                    </>
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          {/* Booking Results */}
          {booking && (
            <div className="tracking-results">
              {/* Booking Summary */}
              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                  <h4 className="mb-0">
                    <i className="fas fa-box me-2"></i>
                    Shipment Details
                  </h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong>Booking Reference:</strong>
                        <div className="text-muted">{booking.bookingReference}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Tracking Number:</strong>
                        <div className="text-muted">{booking.trackingNumber || 'Not assigned yet'}</div>
                      </div>
                      <div className="mb-3">
                        <strong>Status:</strong>
                        <div>
                          <Badge bg={getStatusBadgeVariant(booking.status)} className="fs-6">
                            {formatStatus(booking.status)}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <strong>Route:</strong>
                        <div className="text-muted">
                          {booking.route?.originCity}, {booking.route?.originCountry} 
                          <i className="fas fa-arrow-right mx-2"></i>
                          {booking.route?.destinationCity}, {booking.route?.destinationCountry}
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Estimated Delivery:</strong>
                        <div className="text-muted">
                          {booking.estimatedDeliveryDate ? 
                            new Date(booking.estimatedDeliveryDate).toLocaleDateString() : 
                            'To be determined'
                          }
                        </div>
                      </div>
                      <div className="mb-3">
                        <strong>Number of Bags:</strong>
                        <div className="text-muted">{booking.numberOfBags}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Tracking Timeline */}
              <Card className="shadow-sm">
                <Card.Header>
                  <h4 className="mb-0">
                    <i className="fas fa-route me-2"></i>
                    Tracking Timeline
                  </h4>
                </Card.Header>
                <Card.Body>
                  <div className="tracking-timeline">
                    {getTrackingTimeline(booking).map((item, index) => (
                      <div key={item.status} className={`timeline-item ${item.completed ? 'completed' : ''} ${item.current ? 'current' : ''}`}>
                        <div className="timeline-marker">
                          <i className={item.icon}></i>
                        </div>
                        <div className="timeline-content">
                          <h6 className="mb-1">{item.label}</h6>
                          {item.current && (
                            <small className="text-muted">Current Status</small>
                          )}
                          {item.completed && !item.current && (
                            <small className="text-success">
                              <i className="fas fa-check me-1"></i>
                              Completed
                            </small>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>

              {/* Delivery Information */}
              {booking.status === 'DELIVERED' && (
                <Card className="shadow-sm mt-4 border-success">
                  <Card.Header className="bg-success text-white">
                    <h4 className="mb-0">
                      <i className="fas fa-check-circle me-2"></i>
                      Delivery Completed
                    </h4>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-2">
                      <strong>Delivered on:</strong> {new Date(booking.actualDeliveryDate).toLocaleString()}
                    </p>
                    <p className="mb-0">
                      <strong>Delivered to:</strong> {booking.deliveryAddress}
                    </p>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}

          {/* Help Section */}
          <Card className="mt-4 bg-light">
            <Card.Body className="text-center">
              <h5>Need Help?</h5>
              <p className="text-muted mb-3">
                Can't find your booking or have questions about your shipment?
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Button variant="outline-primary" href="/contact">
                  <i className="fas fa-envelope me-2"></i>
                  Contact Support
                </Button>
                <Button variant="outline-primary" href="tel:+15551234567">
                  <i className="fas fa-phone me-2"></i>
                  Call Us
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default TrackBooking;
